import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  clearMetrics,
  resetMetricsDateFilter,
  setMetricsFilters,
} from 'dashboard/redux/actions/metrics-inquiry';
import { getAdjustedFilters } from 'dashboard/redux/selectors/metrics-inquiry';
import { getAllTemplates } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import {
  loadMetricsTemplateFunnel,
  loadMetricsTemplateFunnelFlow,
  loadMetricsTemplateVolumeSummary,
  setFunnelInquiryTemplateVersionIdLoaded,
  setFunnelTemplateIdLoaded,
} from 'dashboard/redux/thunk-actions/metrics-inquiry';
import DashboardInquiryAnalyticsFunnelV2View from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const DashboardInquiryAnalyticsFunnelRoute = (props: PropsFromRedux) => {
  const {
    currentEnvironment,
    filters,
    templates,
    templateVolumeSummary,
    templateFunnels,
    templateFunnelFlows,
    setFunnelTemplateIdLoaded,
    clearMetrics,
  } = props;

  useEffectOnEnvironmentChange(() => {
    props.clearMetrics();
  });

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsFilters,
  });

  const { templateIds } = filters;

  // Treat no selected templates as 'show all'
  const templatesToShow = useMemo(
    () => templates.filter(({ id }) => templateIds.length === 0 || templateIds.includes(id)),

    [templates, templateIds],
  );

  // V1 callbacks
  const { error: volumeMetricsErrorV1, callback: loadVolumeMetricsV1 } = useApiCallback(
    // templateId is set explicitly for the v2 code path (which only selects 1 template id at a time).
    // otherwise we select multiple
    async ({ templateId }: { templateId?: string }) => {
      const templateIds = templateId != null ? [templateId] : templatesToShow.map(({ id }) => id);
      return props.loadMetricsTemplateVolumeSummary({
        currentEnvironment,
        filters: { ...filters, templateIds: templateIds },
      });
    },
    [currentEnvironment, filters, templatesToShow],
  );

  useApiErrorHandler({
    error: volumeMetricsErrorV1,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiry metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const {
    loading: funnelMetricsLoadingV1,
    error: funnelMetricsErrorV1,
    callback: loadFunnelMetricsV1,
  } = useApiCallback(
    async ({ templateId }: { templateId?: string }) => {
      const templateIds =
        templateId != null
          ? [templateId]
          : templatesToShow.map(({ id }) => id).filter((id) => !id.startsWith('itmpl'));
      return props.loadMetricsTemplateFunnel({
        currentEnvironment,
        filters: {
          ...filters,
          templateIds: templateIds, // don't pass inquiry templates to this endpoint
        },
      });
    },
    [currentEnvironment, filters, templatesToShow],
  );
  useApiErrorHandler({
    error: funnelMetricsErrorV1,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template funnel metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const loadMetricsV1 = useCallback(
    async ({ templateId }: { templateId?: string }) => {
      const volumeMetricsPromise = loadVolumeMetricsV1({ templateId });
      const funnelMetricsPromise = loadFunnelMetricsV1({ templateId });
      await Promise.all([volumeMetricsPromise, funnelMetricsPromise]);
    },
    [loadVolumeMetricsV1, loadFunnelMetricsV1],
  );

  // V2 callbacks
  // Ideally we can only show dynamic flow templates. Unfortunately, we are not there yet and we need
  // to support both :(.
  // selectedTemplate is what is chosen in the dropdown. templateToShow is the template we want to show
  // metrics for.
  const selectedTemplate =
    templates.find((t) => t.id === filters.templateOrInquiryTemplateId) ?? templates[0];
  const templateToShow =
    templates.find((t) => t.id === props.loadedTemplateOrInquiryTemplateId) || selectedTemplate;

  const {
    loading: funnelFlowMetricsLoading,
    error: funnelFlowMetricsError,
    callback: loadFunnelFlowMetrics,
  } = useApiCallback(
    ({ inquiryTemplateId }: { inquiryTemplateId: string }) => {
      return props.loadMetricsTemplateFunnelFlow({
        currentEnvironment,
        filters: {
          ...filters,
          templateOrInquiryTemplateId: inquiryTemplateId,
        },
      });
    },
    [currentEnvironment, filters, templatesToShow],
  );

  useApiErrorHandler({
    error: funnelFlowMetricsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template funnel metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const loadTemplateOrInquiryTemplateMetrics = useCallback(async () => {
    if (selectedTemplate == null) {
      props.showMessage('No accessible templates.', MessageType.Failure);
      return;
    }
    await clearMetrics();
    if (selectedTemplate.type === ApiModel.InquiryTemplate) {
      await loadFunnelFlowMetrics({ inquiryTemplateId: selectedTemplate.id });
    } else if (selectedTemplate.type === ApiModel.Template) {
      await loadMetricsV1({ templateId: selectedTemplate.id });
      await setFunnelTemplateIdLoaded({ templateId: selectedTemplate.id });
    }
  }, [
    templateToShow,
    volumeMetricsErrorV1,
    funnelMetricsErrorV1,
    funnelFlowMetricsError,
    loadFunnelFlowMetrics,
    loadMetricsV1,
    clearMetrics,
  ]);

  // common callbacks
  const handleInquiryTemplateFilterUpdate = useCallback((inquiryTemplateId: string) => {
    props.setMetricsFilters({
      templateOrInquiryTemplateId: inquiryTemplateId,
    });
  }, []);

  const handleInquiryTemplateVersionIdUpdate = useCallback((inquiryTemplateVersionId: string) => {
    props.setFunnelInquiryTemplateVersionIdLoaded({
      inquiryTemplateVersionId: inquiryTemplateVersionId,
    });
  }, []);

  // this is only used for v2 code path.
  useEffect(() => {
    if (
      props.loadedTemplateOrInquiryTemplateId == null ||
      props.loadedTemplateOrInquiryTemplateId !== templateToShow.id
    ) {
      loadTemplateOrInquiryTemplateMetrics();
    }
  }, [selectedTemplate]);

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      // Ensure we have a date range before we extract dates
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({ startDate, endDate })
          : props.resetMetricsDateFilter();
      }
    },
    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  return (
    <DashboardInquiryAnalyticsFunnelV2View
      filters={filters}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleTemplateFilterUpdate={handleInquiryTemplateFilterUpdate}
      isUnpublishedTemplate={
        props.loadedTemplateOrInquiryTemplateId != null && templateFunnelFlows.length === 0
      }
      loading={funnelMetricsLoadingV1 || funnelFlowMetricsLoading}
      loadMetrics={loadTemplateOrInquiryTemplateMetrics}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      queriedAt={props.queriedAt}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updatedAt={props.funnelUpdatedAt}
      templates={templates}
      selectedTemplate={selectedTemplate}
      templateToShow={templateToShow}
      templateFunnelFlows={templateFunnelFlows}
      templateFunnel={templateFunnels[templateToShow?.id]}
      templateMetricsVolumeSummary={templateVolumeSummary[templateToShow?.id]}
      loadedTemplateOrInquiryTemplateId={props.loadedTemplateOrInquiryTemplateId}
      loadedInquiryTemplateVersionId={props.loadedInquiryTemplateVersionId}
      handleInquiryTemplateVersionIdUpdate={handleInquiryTemplateVersionIdUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    templateVolumeSummary: state.metricsInquiry.templateVolumeSummary,
    templateFunnels: state.metricsInquiry.templateFunnels,
    queriedAt: state.metricsInquiry.queriedAt.funnelFlow,
    filters: getAdjustedFilters(state),
    templates: getAllTemplates(state),
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    templateFunnelFlows: state.metricsInquiry?.templateFunnelFlows,
    loadedTemplateOrInquiryTemplateId: state.metricsInquiry.loadedTemplateOrInquiryTemplateId,
    funnelUpdatedAt: state.metricsInquiry.updatedAt.funnelFlow,
    loadedInquiryTemplateVersionId: state.metricsInquiry.loadedInquiryTemplateVersionId,
  };
};

const mapDispatchToProps = {
  clearMetrics,
  loadMetricsTemplateVolumeSummary,
  loadMetricsTemplateFunnel,
  setMetricsFilters,
  resetMetricsDateFilter,
  showMessage,
  loadMetricsTemplateFunnelFlow,
  setFunnelTemplateIdLoaded,
  setFunnelInquiryTemplateVersionIdLoaded,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryAnalyticsFunnelRoute);
